import { XCircleIcon } from '@heroicons/react/24/solid';
import { Component, ErrorInfo, ReactNode, Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { Loader } from '@/components/Loader';
import {
  SettingContentCard,
  SettingSectionHeader,
} from '@/sections/settings/components';
import { UserProfileCards } from '@/sections/settings/components/cards/UserProfileCards';
import { useAuth } from '@/state/auth';

export const dangerButtonStyle =
  'border border-chariot-stroke-critical bg-chariot-status-critical hover:bg-chariot-status-critical/80 disabled:border-transparent';

/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 *
 * Note: Kept local to match the pattern in UserSettingsTab.tsx.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('PraetorianUserSettings error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * PraetorianUserSettings displays user settings for Praetorian staff members.
 *
 * @remarks
 * This page is ONLY accessible to Praetorian users (isPraetorianUser = true).
 * It provides a dedicated settings page that:
 *
 * 1. **Security Layer 1**: Redirects non-Praetorian users to regular settings
 * 2. **Security Layer 2**: Auto-exits impersonation when page loads
 * 3. **Security Layer 3**: Always shows Praetorian's own settings (never customer data)
 *
 * Critical Differences from Regular User Settings Tab:
 * - Standalone page (not a tab in Settings)
 * - Always uses Praetorian's email (me), never impersonated user
 * - Always editable (no impersonation restrictions)
 * - Automatically exits impersonation on load
 * - Redirects non-Praetorian users
 *
 * Fixes:
 * - Replaces broken ApiKeysSection component with working UserProfileCards
 * - Reduces code duplication by reusing shared settings components
 * - Maintains all security features while improving maintainability
 *
 * @see {@link UserProfileCards} Core profile settings implementation
 * @see {@link UserSettingsTab} Regular user settings tab (for customers)
 */
export function PraetorianUserSettings() {
  const navigate = useNavigate();
  const { isImpersonating, isPraetorianUser, stopImpersonation } = useAuth();

  // Security Layer 1: Redirect non-Praetorian users to regular settings
  useEffect(() => {
    if (!isPraetorianUser) {
      navigate(AuthenticatedRoute.SETTINGS + '?tab=userSettings');
    }
  }, [isPraetorianUser, navigate]);

  // Security Layer 2: Auto-exit impersonation when this page loads
  // Ensures Praetorian settings never show customer data
  useEffect(() => {
    if (isImpersonating && isPraetorianUser) {
      stopImpersonation(
        AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS,
        true
      );
    }
  }, [isImpersonating, isPraetorianUser, stopImpersonation]);

  // Security Layer 3: Early return for non-Praetorian users
  if (!isPraetorianUser) {
    return null;
  }

  return (
    <AppRouteBody title="User Settings">
      <div className="flex h-full flex-col gap-6 overflow-auto pr-4">
        <SettingSectionHeader
          title="User Settings"
          description="Manage your Praetorian account profile, authentication, and interface preferences."
        />

        <SettingContentCard data-testid="praetorian-user-settings-card">
          <ErrorBoundary
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                <XCircleIcon className="text-chariot-status-critical size-12" />
                <div className="flex flex-col gap-2 text-center">
                  <h2 className="text-chariot-text-primary text-lg font-bold">
                    Failed to Load User Settings
                  </h2>
                  <p className="text-chariot-text-secondary max-w-md text-sm">
                    Unable to retrieve user settings. Please refresh the page or
                    contact support.
                  </p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                  <Loader type="spinner" isLoading />
                  <p className="text-chariot-text-secondary text-sm">
                    Loading user settings...
                  </p>
                </div>
              }
            >
              {/* UserProfileCards always uses logged-in user (me) internally, always editable */}
              <UserProfileCards />
            </Suspense>
          </ErrorBoundary>
        </SettingContentCard>
      </div>
    </AppRouteBody>
  );
}
